import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  InventorySupplierInvoiceFilterParams,
  InventorySupplierInvoiceLineRecord,
  InventorySupplierInvoiceRecord,
  InventorySupplierInvoiceSummaryCounts,
  SaveSupplierInvoiceRequest,
} from '../types/inventory';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;
const EXPORT_PAGE_SIZE = 1000;

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapInvoiceLine = (
  row: Record<string, unknown>,
): InventorySupplierInvoiceLineRecord => ({
  id: String(row.id || ''),
  supplier_invoice_id: String(row.supplier_invoice_id || ''),
  inventory_item_id: String(row.inventory_item_id || ''),
  description: (row.description as string | null) ?? null,
  quantity_purchased: toNumber(row.quantity_purchased),
  purchase_unit_id: (row.purchase_unit_id as string | null) ?? null,
  conversion_rate: toNumber(row.conversion_rate),
  quantity_in_stock_unit: toNumber(row.quantity_in_stock_unit),
  unit_cost: toNumber(row.unit_cost),
  tax_rate: toNumber(row.tax_rate),
  tax_amount: toNumber(row.tax_amount),
  line_total: toNumber(row.line_total),
  created_at: String(row.created_at || ''),
  updated_at: String(row.updated_at || ''),
});

const mapInvoice = (
  row: Record<string, unknown>,
): InventorySupplierInvoiceRecord => ({
  id: String(row.id || ''),
  company_id: toNumber(row.company_id),
  outlet_id: String(row.outlet_id || ''),
  supplier_id: String(row.supplier_id || ''),
  stock_location_id: String(row.stock_location_id || ''),
  invoice_number: (row.invoice_number as string | null) ?? null,
  invoice_date: String(row.invoice_date || ''),
  due_date: (row.due_date as string | null) ?? null,
  subtotal: toNumber(row.subtotal),
  tax_amount: toNumber(row.tax_amount),
  total_amount: toNumber(row.total_amount),
  amount_paid: toNumber(row.amount_paid),
  status: row.status as InventorySupplierInvoiceRecord['status'],
  notes: (row.notes as string | null) ?? null,
  posted_at: (row.posted_at as string | null) ?? null,
  posted_by: (row.posted_by as string | null) ?? null,
  created_by: (row.created_by as string | null) ?? null,
  created_at: String(row.created_at || ''),
  updated_at: String(row.updated_at || ''),
  lines: Array.isArray(row.supplier_invoice_lines)
    ? (row.supplier_invoice_lines as Record<string, unknown>[]).map(
        mapInvoiceLine,
      )
    : undefined,
});

export const fetchSupplierInvoices = async ({
  companyId,
  outletId,
  supplierId = 'all',
  status = 'all',
  dateFrom,
  dateTo,
  inventoryItemIds = [],
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: InventorySupplierInvoiceFilterParams): Promise<
  ApiResponse<PaginatedResponse<InventorySupplierInvoiceRecord>>
> => {
  try {
    if (inventoryItemIds.length) {
      const invoiceIds = new Set<string>();
      let itemPage = 1;
      let hasMoreItemLines = true;

      while (hasMoreItemLines) {
        const from = (itemPage - 1) * EXPORT_PAGE_SIZE;
        const to = from + EXPORT_PAGE_SIZE - 1;

        // eslint-disable-next-line no-await-in-loop
        const lineResult = await supabase
          .from('supplier_invoice_lines')
          .select('supplier_invoice_id')
          .in('inventory_item_id', inventoryItemIds)
          .range(from, to);

        if (lineResult.error) {
          return {
            success: false,
            error: lineResult.error.message,
            data: undefined,
          };
        }

        const lineRows = lineResult.data || [];
        lineRows.forEach((row) => {
          const invoiceId = String(row.supplier_invoice_id || '');
          if (invoiceId) {
            invoiceIds.add(invoiceId);
          }
        });

        hasMoreItemLines = lineRows.length === EXPORT_PAGE_SIZE;
        itemPage += 1;
      }

      if (!invoiceIds.size) {
        return {
          success: true,
          data: {
            data: [],
            pagination: {
              page,
              pageSize,
              totalItems: 0,
              totalPages: 1,
              hasNextPage: false,
              hasPreviousPage: false,
            },
          },
        };
      }

      let query = supabase
        .from('supplier_invoices')
        .select('*')
        .eq('company_id', companyId)
        .eq('outlet_id', outletId)
        .in('id', Array.from(invoiceIds))
        .order('invoice_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (supplierId && supplierId !== 'all') {
        query = query.eq('supplier_id', supplierId);
      }

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }

      if (dateFrom) {
        query = query.gte('invoice_date', dateFrom);
      }

      if (dateTo) {
        query = query.lte('invoice_date', dateTo);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      const allRows = (data || []).map((row) =>
        mapInvoice(row as Record<string, unknown>),
      );
      const totalItems = allRows.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
      const from = (page - 1) * pageSize;
      const pagedRows = allRows.slice(from, from + pageSize);

      return {
        success: true,
        data: {
          data: pagedRows,
          pagination: {
            page,
            pageSize,
            totalItems,
            totalPages,
            hasNextPage: page < totalPages,
            hasPreviousPage: page > 1,
          },
        },
      };
    }

    let query = supabase
      .from('supplier_invoices')
      .select('*')
      .eq('company_id', companyId)
      .eq('outlet_id', outletId)
      .order('invoice_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (supplierId && supplierId !== 'all') {
      query = query.eq('supplier_id', supplierId);
    }

    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    if (dateFrom) {
      query = query.gte('invoice_date', dateFrom);
    }

    if (dateTo) {
      query = query.lte('invoice_date', dateTo);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const allRows = (data || []).map((row) =>
      mapInvoice(row as Record<string, unknown>),
    );
    const totalItems = allRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const from = (page - 1) * pageSize;
    const pagedRows = allRows.slice(from, from + pageSize);

    return {
      success: true,
      data: {
        data: pagedRows,
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch supplier invoices',
      data: undefined,
    };
  }
};

export const fetchSupplierInvoiceDetail = async (
  companyId: number,
  invoiceId: string,
): Promise<ApiResponse<InventorySupplierInvoiceRecord>> => {
  try {
    const { data, error } = await supabase
      .from('supplier_invoices')
      .select('*, supplier_invoice_lines(*)')
      .eq('company_id', companyId)
      .eq('id', invoiceId)
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return {
      success: true,
      data: mapInvoice(data as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch supplier invoice',
      data: undefined,
    };
  }
};

export const fetchSupplierInvoiceSummary = async (
  companyId: number,
  outletId: string,
  supplierId: string = 'all',
  status: string = 'all',
  dateFrom: string = '',
  dateTo: string = '',
  inventoryItemIds: string[] = [],
): Promise<ApiResponse<InventorySupplierInvoiceSummaryCounts>> => {
  try {
    const result = await fetchSupplierInvoices({
      companyId,
      outletId,
      supplierId,
      status: status as InventorySupplierInvoiceFilterParams['status'],
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      inventoryItemIds,
      page: 1,
      pageSize: 5000,
    });

    if (!result.success) {
      return { success: false, error: result.error, data: undefined };
    }

    const invoices = result.data?.data ?? [];
    const draftInvoices = invoices.filter(
      (inv) => inv.status === 'draft',
    ).length;
    const postedInvoices = invoices.filter(
      (inv) => inv.status !== 'draft' && inv.status !== 'voided',
    ).length;
    const totalValue = invoices
      .filter((inv) => inv.status !== 'voided')
      .reduce((sum, inv) => sum + inv.total_amount, 0);
    const totalOutstanding = invoices
      .filter(
        (inv) => inv.status === 'posted' || inv.status === 'partially_paid',
      )
      .reduce((sum, inv) => sum + (inv.total_amount - inv.amount_paid), 0);

    return {
      success: true,
      data: {
        totalInvoices: invoices.length,
        draftInvoices,
        postedInvoices,
        totalValue,
        totalOutstanding,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch supplier invoice summary',
      data: undefined,
    };
  }
};

export const fetchSupplierInvoiceExportRows = async (
  params: Omit<InventorySupplierInvoiceFilterParams, 'page' | 'pageSize'>,
): Promise<ApiResponse<InventorySupplierInvoiceRecord[]>> => {
  try {
    const rows: InventorySupplierInvoiceRecord[] = [];
    let page = 1;
    let hasNextPage = true;

    while (hasNextPage) {
      // eslint-disable-next-line no-await-in-loop
      const result = await fetchSupplierInvoices({
        ...params,
        page,
        pageSize: EXPORT_PAGE_SIZE,
      });

      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch supplier invoice export rows',
          data: undefined,
        };
      }

      rows.push(...result.data.data);
      hasNextPage = result.data.pagination.hasNextPage;
      page += 1;

      if (!result.data.data.length) {
        break;
      }
    }

    return { success: true, data: rows };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch supplier invoice export rows',
      data: undefined,
    };
  }
};

export const saveSupplierInvoice = async (
  request: SaveSupplierInvoiceRequest,
): Promise<ApiResponse<InventorySupplierInvoiceRecord>> => {
  try {
    const isEdit = !!request.id;
    const headerPayload = {
      company_id: request.companyId,
      outlet_id: request.outletId,
      supplier_id: request.supplierId,
      stock_location_id: request.stockLocationId,
      invoice_number: request.invoiceNumber ?? null,
      invoice_date: request.invoiceDate,
      due_date: request.dueDate ?? null,
      subtotal: request.subtotal,
      tax_amount: request.taxAmount,
      total_amount: request.totalAmount,
      notes: request.notes ?? null,
      updated_at: new Date().toISOString(),
    };

    let invoiceId: string;

    if (isEdit) {
      const { data, error } = await supabase
        .from('supplier_invoices')
        .update(headerPayload)
        .eq('id', request.id!)
        .eq('company_id', request.companyId)
        .eq('status', 'draft')
        .select()
        .single();

      if (error || !data) {
        return {
          success: false,
          error: error?.message ?? 'Failed to update invoice',
          data: undefined,
        };
      }

      invoiceId = (data as Record<string, unknown>).id as string;

      // Replace all lines on edit
      await supabase
        .from('supplier_invoice_lines')
        .delete()
        .eq('supplier_invoice_id', invoiceId);
    } else {
      const { data, error } = await supabase
        .from('supplier_invoices')
        .insert(headerPayload)
        .select()
        .single();

      if (error || !data) {
        return {
          success: false,
          error: error?.message ?? 'Failed to create invoice',
          data: undefined,
        };
      }

      invoiceId = (data as Record<string, unknown>).id as string;
    }

    // Insert lines
    if (request.lines.length > 0) {
      const lineRows = request.lines.map((line) => ({
        supplier_invoice_id: invoiceId,
        inventory_item_id: line.inventoryItemId,
        description: line.description ?? null,
        quantity_purchased: line.quantityPurchased,
        purchase_unit_id: line.purchaseUnitId ?? null,
        conversion_rate: line.conversionRate,
        quantity_in_stock_unit: line.quantityInStockUnit,
        unit_cost: line.unitCost,
        tax_rate: line.taxRate ?? 0,
        tax_amount: line.taxAmount ?? 0,
        line_total: line.lineTotal,
      }));

      const { error: linesError } = await supabase
        .from('supplier_invoice_lines')
        .insert(lineRows);

      if (linesError) {
        return {
          success: false,
          error: linesError.message,
          data: undefined,
        };
      }
    }

    return fetchSupplierInvoiceDetail(request.companyId, invoiceId);
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save supplier invoice',
      data: undefined,
    };
  }
};

export const postSupplierInvoice = async (
  companyId: number,
  invoiceId: string,
): Promise<ApiResponse<{ invoiceId: string }>> => {
  try {
    const { data, error } = await supabase.rpc('post_supplier_invoice', {
      p_invoice_id: invoiceId,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const result = data as { success: boolean; error?: string };
    if (!result.success) {
      return {
        success: false,
        error: result.error ?? 'Failed to post invoice',
        data: undefined,
      };
    }

    return { success: true, data: { invoiceId } };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to post invoice',
      data: undefined,
    };
  }
};

export const deleteSupplierInvoice = async (
  companyId: number,
  invoiceId: string,
): Promise<ApiResponse<void>> => {
  try {
    const { error } = await supabase
      .from('supplier_invoices')
      .delete()
      .eq('id', invoiceId)
      .eq('company_id', companyId)
      .eq('status', 'draft');

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete supplier invoice',
      data: undefined,
    };
  }
};
